import { useEffect, useRef, useState } from "react";
import type { CardProps } from "@karrtopelka/cards/types";
import type { RenderAssets } from "@karrtopelka/cards/render-client";
import { haptic } from "../haptics.js";
import {
  downloadCard,
  shareCapability,
  shareCard,
  shareCardNative,
  type ShareCapability,
} from "../share.js";
import { track } from "../track.js";

/**
 * Renders the result card (Satori→resvg→PNG, client-side) and drives the share flow.
 * Shared by every game; parameterized by the card props, the reply link, the post
 * caption, and an app-local asset loader (each app serves its own fonts/wasm).
 *
 * HARD RULES (IDEA.md): the image IS the post; the link is reply-only and never in the
 * post body; no X share-intent; provocation only.
 *
 * Adaptive: mobile (Web Share API) → one-tap native sheet with card + caption; desktop
 * → copy the image to paste.
 */
export function ShareSheet({
  card,
  replyLink,
  caption,
  loadAssets,
}: {
  card: CardProps;
  replyLink: string;
  /** Post text (with the concrete number), e.g. "147ms. bet none of you beat it". */
  caption: string;
  /** App-local fetch of the render assets (fonts + wasm from the app's own origin). */
  loadAssets: () => Promise<RenderAssets>;
}) {
  const [png, setPng] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cap, setCap] = useState<ShareCapability>("clipboard");
  const [imageCopied, setImageCopied] = useState(false);
  const [shared, setShared] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ renderCardPng }, assets] = await Promise.all([
          import("@karrtopelka/cards/render-client"),
          loadAssets(),
        ]);
        const blob = await renderCardPng(card, assets);
        if (!alive) return;
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setPng(blob);
        setPreviewUrl(url);
        setCap(shareCapability(blob)); // native on mobile, clipboard on desktop
        track(card.game, "card_ready");
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "render failed");
      }
    })();
    return () => {
      alive = false;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [card, loadAssets]);

  const markKnows = () => localStorage.setItem("kp:knowsShare", "1");

  // Mobile: open the native share sheet (card image + caption, no URL).
  const onNativeShare = async () => {
    if (!png) return;
    haptic.tap();
    const res = await shareCardNative({ png, provocation: caption, replyLink });
    if (res.ok) {
      setShared(true);
      markKnows();
      track(card.game, "share_done", undefined, "native");
    }
  };

  // Desktop: copy ONLY the image so cmd/ctrl+V pastes the card.
  const onCopyImage = async () => {
    if (!png) return;
    const res = await shareCard({ png, provocation: caption, replyLink });
    setImageCopied(res.ok);
    if (res.ok) {
      markKnows();
      track(card.game, "share_done", undefined, "clipboard");
    }
  };

  const onDownload = () => {
    if (!png) return;
    downloadCard(png);
    track(card.game, "share_done", undefined, "download");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(replyLink);
      setLinkCopied(true);
      track(card.game, "link_copy");
    } catch {
      setLinkCopied(false);
    }
  };

  const isNative = cap === "native";

  return (
    <div className="flex w-full max-w-md flex-col items-center gap-4">
      {error ? (
        <p className="text-sm text-accent">card error: {error}</p>
      ) : previewUrl ? (
        <img
          src={previewUrl}
          alt="your result card"
          className="w-full rounded-lg border border-border"
        />
      ) : (
        <div className="flex aspect-[16/9] w-full items-center justify-center rounded-lg border border-border text-sm text-text-faint">
          <span className="animate-pulse">rendering card…</span>
        </div>
      )}

      <div className="flex w-full flex-col gap-2">
        {isNative ? (
          <>
            <button
              type="button"
              onClick={onNativeShare}
              disabled={!png}
              className="rounded-md bg-accent px-4 py-3 text-base font-semibold text-bg active:scale-[0.98] disabled:opacity-40"
            >
              {shared ? "✓ shared — link goes in your reply" : "share your card"}
            </button>
            {png && (
              <button
                type="button"
                onClick={onDownload}
                className="rounded-md border border-border px-4 py-2 text-sm text-text-muted active:scale-[0.98]"
              >
                save image
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onCopyImage}
              disabled={!png}
              className="rounded-md bg-accent px-4 py-3 font-semibold text-bg active:scale-[0.98] disabled:opacity-40"
            >
              {imageCopied ? "✓ image copied — paste as your post" : "copy card image"}
            </button>
            {png && !imageCopied && (
              <button
                type="button"
                onClick={onDownload}
                className="rounded-md border border-border px-4 py-2 text-sm text-text-muted hover:text-text"
              >
                or download the card
              </button>
            )}
          </>
        )}
      </div>

      {/* Reply link surfaces ONLY after the card is shared/copied — the moment it's
          needed (first reply, never the post body). Until then the UI stays clean. */}
      {(shared || imageCopied) && (
        <button
          type="button"
          onClick={copyLink}
          className="text-xs text-text-faint active:opacity-60 hover:text-text-muted"
        >
          reply with <span className="font-mono text-text-muted">{replyLink}</span>
          {linkCopied ? " ✓ copied" : " · tap to copy"}
        </button>
      )}
    </div>
  );
}
